import {
  hashKey,
  QueryFunctionContext,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { STATUS_CODES } from "../../../enums";
import { AnalysisStatus } from "../../../lib/enums";
import {
  ErrorResponse,
  ValidationErrorResponse
} from "../../../requests/@types";
import { NetworkSearchResponse } from "../../../services/@types";
import { useReducedSystemHeathStatus } from "../../header/system-health/hooks/useSystemHealthStatus";
import { advancedSearchKeys } from "../../samples/queryKeys";
import api from "../api";
import {
  filterByClassification,
  filterByDate,
  filterByResourceType,
  formatNetworkSelections,
  isNetworkOnlySearch,
  sortNetworkReferences
} from "../components/network/utils";
import { LOCATION } from "../consts";
import {
  constructNetworkSelections,
  extractNetworkValuesFromQuery,
  hasOnlyNonNTIKeywords,
  isAirGappedMode,
  showCloudUnreachableOrNoAccessNotifications
} from "../util";
import { SAMPLES_REFETCH_INTERVAL, useQueryValidationContext } from "./index";
import useSearchContext from "./useSearchContext";

const FETCH_URLS_IN_ANALYZING_STATE_REFETCH_INTERVAL = 5000;

const ACCESS_ERROR_CODES = [STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED];

const CLOUD_UNAVAILABLE_REASON = "cloud_unavailable";

type NetworkSearchErrorResponse = ValidationErrorResponse & {
  reason?: string;
};

const useNetworkSearch = () => {
  const cachedNetworkRefs = useRef<{ key: string; refs?: string[] }>({
    key: ""
  });

  const queryClient = useQueryClient();

  const { location: params, navigate } = useSearchContext();

  const {
    data: validationData,
    isLoading: isValidating,
    error: validationError
  } = useQueryValidationContext();

  const { isCloudDisabled } = useReducedSystemHeathStatus();

  const { q, page, pagesize, selections, sort, location } = params;

  const isNetwork = location === LOCATION.NETWORK;

  const formattedSelections = formatNetworkSelections(q, selections, isNetwork);

  const {
    classification: { value: classification = "" } = {},
    network_resource: { value: type = "" } = {},
    time_type: { value: timeType = "" } = {},
    period_type: periodType
  } = formattedSelections ?? {};

  const { networkValues, tooLongNetworkValues } = useMemo(
    () => extractNetworkValuesFromQuery(q ?? ""),
    [q]
  );

  const pageSize = isNetwork ? pagesize : 0;
  const userIsSearching = Boolean(networkValues.length);
  const returnNoData = hasOnlyNonNTIKeywords(q, networkValues);
  const isNetworkOnly = isNetworkOnlySearch(q ?? "", networkValues);

  const networkSelections = constructNetworkSelections(formattedSelections);

  const shouldRedirect = isNetworkOnly && location !== LOCATION.NETWORK;

  useEffect(() => {
    if (!shouldRedirect) return;
    navigate({ location: LOCATION.NETWORK }, { replace: true });
  }, [shouldRedirect, navigate]);

  const networkQueryKey = advancedSearchKeys.fetchNetworkDataKey(
    q,
    networkSelections
  );

  const search = async ({ signal, queryKey }: QueryFunctionContext) => {
    const keyHash = hashKey(queryKey);
    const { key: cachedKey, refs } = cachedNetworkRefs.current;
    const { data } = await api.retrieveNetworkSearchData(
      {
        network_references: networkValues,
        cached_references: userIsSearching || cachedKey !== keyHash ? [] : refs,
        ...networkSelections
      },
      signal
    );
    cachedNetworkRefs.current = { key: keyHash, refs: data?.cached_references };
    return data;
  };

  const {
    data: networkData,
    error: networkError,
    isError,
    isLoading
  } = useQuery<
    NetworkSearchResponse,
    ErrorResponse<NetworkSearchErrorResponse>
  >({
    queryKey: networkQueryKey,
    queryFn: search,
    enabled:
      Boolean(validationData?.is_valid) &&
      !isCloudDisabled &&
      !isAirGappedMode() &&
      (isNetwork || !returnNoData),
    staleTime: SAMPLES_REFETCH_INTERVAL,
    refetchInterval: userIsSearching ? false : SAMPLES_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  const { data: urlsInAnalysisStateData } = useQuery({
    queryKey: advancedSearchKeys.fetchListOfUrlsInAnalysisStatus,
    queryFn: api.fetchListOfUrlsInAnalysisStatus,
    enabled: Boolean(networkData?.entries?.length),
    refetchInterval: FETCH_URLS_IN_ANALYZING_STATE_REFETCH_INTERVAL
  });

  const { urls } = urlsInAnalysisStateData?.data ?? {};

  // Aborts the in-flight request and restores the previous data
  const abortQuery = () => {
    queryClient.cancelQueries({ queryKey: networkQueryKey });
  };

  const { status: errorStatus } = networkError ?? {};

  useEffect(() => {
    if (errorStatus && ACCESS_ERROR_CODES.includes(errorStatus)) {
      showCloudUnreachableOrNoAccessNotifications(null);
    }
  }, [errorStatus]);

  const { returnData, totalCount } = useMemo(() => {
    if (returnNoData) return { returnData: [], totalCount: 0 };

    const networkDataEntries = networkData?.entries?.map(item => {
      const isAnalyzing = urls?.includes(item.requested_network_location);
      return {
        ...item,
        classification: isAnalyzing
          ? AnalysisStatus.PROCESSING
          : item.classification,
        threat_level: isAnalyzing ? undefined : item.threat_level
      };
    });

    const data = sortNetworkReferences(
      (networkDataEntries ?? [])
        .filter(item => filterByClassification(item, classification))
        .filter(item => filterByResourceType(item, type))
        .filter(item => filterByDate(item, timeType, periodType)),
      sort
    );

    const index = (page - 1) * pageSize;

    return {
      returnData: data.slice(index, pageSize + index),
      totalCount: data.length
    };
  }, [
    classification,
    networkData?.entries,
    page,
    pageSize,
    periodType,
    returnNoData,
    sort,
    timeType,
    type,
    urls
  ]);

  const combinedInvalid = [
    ...tooLongNetworkValues,
    ...(networkData?.invalid_network_locations ?? [])
  ];

  const invalidNetworkData = returnNoData ? [] : combinedInvalid;

  const isFetching = isValidating || isLoading;
  const isIgnorableError = errorStatus === STATUS_CODES.NOT_CHANGED;

  const shouldShow =
    (!isError || isIgnorableError) && !validationError && !isCloudDisabled;

  const errorMessage = isError && !isIgnorableError ? networkError : undefined;

  const isCloudUnavailable =
    isCloudDisabled ||
    isAirGappedMode() ||
    networkError?.data?.reason === CLOUD_UNAVAILABLE_REASON;

  return {
    data: returnData,
    isFetching,
    totalCount,
    show: shouldShow,
    invalidNetworkData,
    abortQuery,
    error: errorMessage,
    isCloudUnavailable
  };
};

export default useNetworkSearch;
